import { z } from "zod";
import { PAKISTAN_PROVINCES } from "../constants/provinces.js";

export const shippingAddressSchema = z.object({
  line1: z.string().min(3),
  line2: z.string().optional(),
  city: z.string().min(2),
  province: z.enum(PAKISTAN_PROVINCES),
  postalCode: z.string().min(3),
  country: z.string().min(2)
});

export const placeOrderSchema = z.object({
  shippingAddress: shippingAddressSchema,
  contactPhone: z.string().min(7),
  discountCode: z.string().trim().min(1).optional(),
  coinsToUse: z.number().int().min(0).optional(),
  // The server still computes every amount. This is only a choice of rail.
  paymentMethod: z.enum(["ADVANCE_COD", "SAFEPAY"]).optional()
});
export type PlaceOrderInput = z.infer<typeof placeOrderSchema>;

export const trackOrderSchema = z.object({
  orderNumber: z.string().trim().min(1),
  phone: z.string().min(7)
});
export type TrackOrderInput = z.infer<typeof trackOrderSchema>;

export const updateOrderStatusSchema = z.object({
  status: z.enum(["PENDING", "CONFIRMED", "PROCESSING", "SHIPPED", "DELIVERED", "CANCELLED"]),
  note: z.string().optional()
});
export type UpdateOrderStatusInput = z.infer<typeof updateOrderStatusSchema>;

export const updateOrderTrackingSchema = z.object({
  trackingNumber: z.string().trim().min(1).max(120).nullable()
});
export type UpdateOrderTrackingInput = z.infer<typeof updateOrderTrackingSchema>;

export const updateStoreSettingsSchema = z.object({
  storeName: z.string().trim().min(1).max(80).nullable(),
  logoUrl: z.string().trim().min(1).nullable(),
  lowStockThreshold: z.number().int().min(0),
  bannerImageUrl: z.string().trim().min(1).nullable(),
  bannerText: z.string().trim().max(140).nullable(),
  bannerLink: z.string().trim().min(1).nullable(),
  bannerActive: z.boolean(),
  gmailUser: z.string().trim().email().nullable(),
  // Blank/omitted means "keep the currently saved password" — the admin
  // form never receives the real value back, so it only sends one when
  // actually changing it.
  gmailAppPassword: z.string().trim().optional(),
  adminNotificationEmail: z.string().trim().email().nullable(),
  whatsappNumber: z.string().trim().min(10).nullable(),
  jazzCashNumber: z.string().trim().min(10).nullable(),
  jazzCashAccountTitle: z.string().trim().min(1).nullable(),
  bankName: z.string().trim().min(1).nullable(),
  bankAccountNumber: z.string().trim().min(1).nullable(),
  bankAccountTitle: z.string().trim().min(1).nullable(),
  // Optional — many local banks in Pakistan aren't IBAN-issuing, so this is
  // shown only when the admin has actually set one.
  bankIban: z.string().trim().min(1).nullable(),
  // Not secret — safe to expose to the frontend as-is.
  googleClientId: z.string().trim().min(1).nullable(),
  // Free-text homepage stats the admin sets themselves (e.g. "10,000+",
  // "24/7") — never computed or guessed by the app.
  statsActive: z.boolean(),
  statsHappyCustomers: z.string().trim().max(40).nullable(),
  statsOrdersDelivered: z.string().trim().max(40).nullable(),
  statsQualityProducts: z.string().trim().max(40).nullable(),
  statsSupportAvailability: z.string().trim().max(40).nullable()
});
export type UpdateStoreSettingsInput = z.infer<typeof updateStoreSettingsSchema>;
